import {
  Experimental_CssVarsProvider as CssVarsProvider,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useMemo } from 'react';

import { useAppThemeContext } from '../context';
import { createAppTheme } from './createAppTheme';
// options
import RTL from './options/right-to-left';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export const ThemeProvider = ({ children }: Props) => {
  const settings = useAppThemeContext();

  // The whole build is memoized, not just its input: `createTheme` /
  // `extendTheme` / `componentsOverrides` are not cheap, and a fresh theme
  // object on every render is a fresh identity for every styled component
  // below it.
  // Rebuilt when the RESOLVED palette moves (tenant load, org switch), not just
  // on contrast/direction: the JS palette is built from those vars now, so a
  // stale theme here is a theme that disagrees with the CSS vars on `:root`.
  const { cssVarsTheme, theme } = useMemo(
    () =>
      createAppTheme({
        themeContrast: settings.themeContrast,
        themeDirection: settings.themeDirection,
        vars: settings.theme,
      }),
    [settings.themeContrast, settings.themeDirection, settings.theme],
  );

  return (
    <MuiThemeProvider theme={theme}>
      <CssVarsProvider theme={cssVarsTheme}>
        <RTL themeDirection={settings.themeDirection}>
          <CssBaseline />
          {children}
        </RTL>
      </CssVarsProvider>
    </MuiThemeProvider>
  );
};
